/** Sage sidebar — white nav labels/icons on deep green with gold active indicator. */
export const sidebarDrawerPaperSx = {
  boxSizing: 'border-box',
  bgcolor: 'primary.main',
  backgroundImage: 'linear-gradient(180deg, #1A2E1A 0%, #142214 55%, #0F1A0F 100%)',
  color: 'common.white',
  borderRight: 'none',
  boxShadow: '4px 0 32px rgba(26, 46, 26, 0.18)',
  '& .MuiDivider-root': { borderColor: 'rgba(255,255,255,0.1)' },
  '& .MuiListItemButton-root': {
    borderRadius: 2.5,
    py: 1.15,
    px: 1.5,
    color: '#FFFFFF',
    transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    '& .MuiListItemIcon-root': {
      color: 'rgba(255,255,255,0.88) !important',
      minWidth: 40,
      transition: 'color 0.2s ease',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 500,
      fontSize: '0.9rem',
      letterSpacing: '0.01em',
      color: 'rgba(255,255,255,0.88) !important',
    },
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.1)',
      color: '#FFFFFF',
      '& .MuiListItemIcon-root': { color: '#FFFFFF !important' },
      '& .MuiListItemText-primary': { color: '#FFFFFF !important' },
    },
    '&.Mui-selected': {
      bgcolor: 'rgba(255,255,255,0.14)',
      color: '#FFFFFF',
      boxShadow: 'inset 3px 0 0 var(--gold)',
      '& .MuiListItemIcon-root': { color: '#FFFFFF' },
      '& .MuiListItemText-primary': { fontWeight: 700, color: '#FFFFFF' },
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.18)',
        '& .MuiListItemText-primary': { color: '#FFFFFF' },
      },
    },
  },
  '& .MuiListItemButton-root.Mui-selected': {
    bgcolor: 'rgba(255,255,255,0.14) !important',
    color: '#FFFFFF !important',
    boxShadow: 'inset 3px 0 0 var(--gold)',
    '& .MuiListItemIcon-root': { color: '#FFFFFF !important' },
    '& .MuiListItemText-primary': {
      fontWeight: '700 !important',
      color: '#FFFFFF !important',
    },
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.18) !important',
    },
  },
} as const;

/** @deprecated Styles are applied via sidebarDrawerPaperSx nested selectors. */
export const sidebarNavItemSx = {} as const;

export const dashboardAppBarSx = {
  bgcolor: 'rgba(255,255,255,0.92)',
  backdropFilter: 'blur(12px)',
  color: 'text.primary',
  borderBottom: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 0 rgba(26, 46, 26, 0.04)',
} as const;

export const dashboardMainSx = {
  flexGrow: 1,
  bgcolor: 'background.default',
  backgroundImage:
    'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(200, 169, 110, 0.06), transparent), radial-gradient(ellipse 60% 40% at 100% 0%, rgba(26, 46, 26, 0.04), transparent)',
} as const;

/** Elevated white panel for dashboard cards and tables. */
export const dashboardPanelSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  backgroundImage: 'none',
  transition: 'box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
} as const;

/** Dark sage hero strip for dashboard welcome areas. */
export const dashboardHeroSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 'var(--radius-xl)',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'linear-gradient(125deg, #1A2E1A 0%, #243D24 48%, #3D5C3D 100%)',
  boxShadow: 'var(--shadow-lg)',
  color: '#FFFFFF',
  '& .MuiTypography-h4': { color: '#FFFFFF' },
  '& .MuiTypography-root.MuiTypography-h4': { color: '#FFFFFF' },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -60,
    right: -40,
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(200,169,110,0.22) 0%, transparent 68%)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -80,
    left: -40,
    width: 220,
    height: 220,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
} as const;

/** Light premium hero for dashboard pages that need consistent white banners. */
export const dashboardLightHeroSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 'var(--radius-xl)',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  backgroundImage: `
    linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(249,247,242,0.96) 100%),
    radial-gradient(circle at top right, rgba(200,169,110,0.14) 0%, transparent 30%),
    radial-gradient(circle at bottom left, rgba(26,46,26,0.08) 0%, transparent 32%)
  `,
  boxShadow: 'var(--shadow-lg)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      linear-gradient(120deg, transparent 0%, rgba(200,169,110,0.08) 48%, transparent 100%),
      repeating-linear-gradient(
        -35deg,
        rgba(26,46,26,0.03) 0 2px,
        transparent 2px 18px
      )
    `,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -40,
    right: -30,
    width: 220,
    height: 220,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(200,169,110,0.18) 0%, rgba(200,169,110,0.06) 42%, transparent 72%)',
    pointerEvents: 'none',
  },
} as const;

/** Wrapper panel grouping a dashboard section's content. */
export const dashboardSectionPanelSx = {
  ...dashboardPanelSx,
  p: { xs: 2, sm: 2.5, md: 3 },
  boxShadow: 'var(--shadow-md)',
} as const;

/** Sage/gold accent panel for highlighted dashboard stats or snapshots. */
export const dashboardAccentPanelSx = {
  ...dashboardSectionPanelSx,
  border: '1px solid rgba(26, 46, 26, 0.12)',
  backgroundImage:
    'linear-gradient(180deg, rgba(26, 46, 26, 0.06) 0%, rgba(200, 169, 110, 0.05) 100%)',
} as const;

export const DASHBOARD_PAGE_MAX_WIDTH = 1180;

/** Consistent max-width wrapper for guide dashboard page content. */
export const dashboardPageSx = {
  width: '100%',
  maxWidth: DASHBOARD_PAGE_MAX_WIDTH,
  mx: 'auto',
} as const;

/** Elevated white surface — use on Paper/Card when sitting on cream backgrounds. */
export const surfacePaperSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  backgroundImage: 'none',
} as const;
